/* import __COLOCATED_TEMPLATE__ from './scim.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { taskFor } from 'ember-concurrency-ts';

import { patchRequest } from 'embercom/lib/inbox/requests';
import type Session from 'embercom/services/session';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type ScimGroupService from 'embercom/services/scim-group-service';

import type { ScimGroup, ScimArgs, PendingChange } from './scim/types';
import { SCIM_TRANSLATION_KEYS } from './scim/constants';
import {
  SCIM_TABLE_COLUMNS,
  CHANGES_REVIEW_TABLE_COLUMNS,
  getRoleName,
  getUserInitials,
  createRoleDropdownList,
  arraysEqual,
  sortGroupsByOrder,
} from './scim/utils';

export default class Scim extends Component<ScimArgs> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare session: Session;
  @service declare store: Store;
  @service declare appService: $TSFixMe;
  @service declare scimGroupService: ScimGroupService;

  @tracked scimGroups: ScimGroup[] = [];
  @tracked isLoading = false;
  @tracked groupChanges: Record<string, { role_id: number | null }> = {};
  @tracked originalGroupOrder: string[] = [];
  @tracked currentGroupOrder: string[] = [];
  @tracked showDetailsModal = false;
  @tracked selectedGroupForDetails: ScimGroup | null = null;
  @tracked isLoadingGroupDetails = false;
  @tracked showApplyChangesModal = false;
  @tracked includedGroupChanges: Record<string, boolean> = {};
  @tracked includeOrderChanges = true;
  @tracked currentAdminGroups: { id: number; name: string; priority?: number }[] = [];
  @tracked isLoadingUserGroups = false;

  constructor(owner: unknown, args: ScimArgs) {
    super(owner, args);
    this.loadScimGroups();
    this.loadCurrentAdminGroups();
  }

  get availableRoles() {
    return this.args.roles || [];
  }

  get scimGroupsTableColumns() {
    return SCIM_TABLE_COLUMNS(this.intl);
  }

  get changesReviewTableColumns() {
    return CHANGES_REVIEW_TABLE_COLUMNS(this.intl);
  }

  get hasUnsavedChanges(): boolean {
    return (
      Object.keys(this.groupChanges).length > 0 ||
      !arraysEqual(this.originalGroupOrder, this.currentGroupOrder)
    );
  }

  get sortedScimGroups(): ScimGroup[] {
    return sortGroupsByOrder(this.scimGroups, this.currentGroupOrder);
  }

  get hasGroups(): boolean {
    return this.scimGroups.length > 0;
  }

  get hasCurrentGroupChanges(): boolean {
    return this.selectedGroupForDetails
      ? Boolean(this.groupChanges[this.selectedGroupForDetails.id])
      : false;
  }

  get hasIncludedChanges(): boolean {
    return Object.values(this.includedGroupChanges).some(Boolean) || this.includeOrderChanges;
  }

  get modalRoleDropdownList() {
    if (!this.selectedGroupForDetails) {
      return [];
    }
    return createRoleDropdownList(this.availableRoles, this.intl, this.updateRoleInModal);
  }

  get currentModalRoleName(): string {
    return this.selectedGroupForDetails
      ? this.getCurrentRoleNameForGroup(this.selectedGroupForDetails.id)
      : '';
  }

  get pendingChangesForReview(): PendingChange[] {
    let changes: PendingChange[] = [];

    if (!arraysEqual(this.originalGroupOrder, this.currentGroupOrder)) {
      changes.push({
        type: 'order' as const,
        id: 'order',
        title: this.intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.ORDER_CHANGE_TITLE),
        oldValue: '',
        newValue: '',
        included: this.includeOrderChanges,
      });
    }

    for (let [groupIdStr, change] of Object.entries(this.groupChanges)) {
      let group = this.scimGroups.find((g) => g.id === groupIdStr);
      if (group) {
        changes.push({
          type: 'role' as const,
          id: groupIdStr,
          title: group.name,
          oldValue: getRoleName(group.role_id, this.availableRoles, this.intl),
          newValue: getRoleName(change.role_id, this.availableRoles, this.intl),
          included: this.includedGroupChanges[groupIdStr] || false,
        });
      }
    }

    return changes;
  }

  @action
  getCurrentRoleForGroup(groupId: string | number): number | null {
    let groupIdStr = groupId.toString();
    return (
      this.groupChanges[groupIdStr]?.role_id ??
      this.scimGroups.find((g) => g.id === groupIdStr)?.role_id ??
      null
    );
  }

  @action
  getCurrentRoleNameForGroup(groupId: string | number): string {
    return getRoleName(this.getCurrentRoleForGroup(groupId), this.availableRoles, this.intl);
  }

  @action
  getRoleDropdownList(groupId: string | number) {
    return createRoleDropdownList(this.availableRoles, this.intl, (roleId) =>
      this.updateGroupRole(groupId, roleId),
    );
  }

  @action
  updateGroupRole(groupId: string | number, roleId: number | null) {
    let groupIdStr = groupId.toString();
    let originalGroup = this.scimGroups.find((g) => g.id === groupIdStr);

    if (originalGroup?.role_id === roleId) {
      let { [groupIdStr]: _removed, ...remaining } = this.groupChanges;
      this.groupChanges = remaining;
    } else {
      this.groupChanges = { ...this.groupChanges, [groupIdStr]: { role_id: roleId } };
    }
  }

  @action
  getUserInitials(user: Parameters<typeof getUserInitials>[0]) {
    return getUserInitials(user);
  }

  @action
  getGroupPriority(group: ScimGroup): number {
    return this.sortedScimGroups.findIndex((g) => g.id === group.id) + 1;
  }

  @action
  reorderGroups(reorderedGroups: ScimGroup[]) {
    this.currentGroupOrder = reorderedGroups.map((group) => group.id);
  }

  @action
  async openGroupDetails(group: ScimGroup) {
    this.selectedGroupForDetails = group;
    this.showDetailsModal = true;
    this.isLoadingGroupDetails = true;

    try {
      await this.loadGroupDetails(parseInt(group.id, 10));
    } catch (error) {
      console.error('Failed to load group details:', error);
      this.notificationsService.notifyError(
        this.intl.t(SCIM_TRANSLATION_KEYS.TAB.DETAILS_LOAD_ERROR),
      );
    } finally {
      this.isLoadingGroupDetails = false;
    }
  }

  @action
  closeDetailsModal() {
    this.showDetailsModal = false;
    this.selectedGroupForDetails = null;
  }

  @action
  updateRoleInModal(roleId: number | null) {
    if (!this.selectedGroupForDetails) {
      return;
    }

    this.updateGroupRole(this.selectedGroupForDetails.id, roleId);
    if (roleId !== null) {
      this.selectedGroupForDetails.role_id = roleId;
    }
  }

  @action
  async applyCurrentGroupChanges() {
    if (!this.selectedGroupForDetails) {
      return;
    }

    let groupId = this.selectedGroupForDetails.id;
    let hasChanges = this.groupChanges[groupId];
    if (!hasChanges) {
      return;
    }

    this.isLoading = true;
    try {
      await this.bulkUpdateScimGroups({ mapping: { [groupId]: hasChanges.role_id } });

      let { [groupId]: _removed, ...remaining } = this.groupChanges;
      this.groupChanges = remaining;

      this.notificationsService.notifyConfirmation(
        this.intl.t(SCIM_TRANSLATION_KEYS.SUCCESS_MESSAGES.GROUP_CHANGES_APPLIED),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'scim_group_role_single',
        section: 'settings',
        place: 'workspace_teammates',
        group_id: parseInt(groupId, 10),
        role_id: hasChanges.role_id?.toString() || null,
      });

      await this.loadScimGroups();
      await this.loadGroupDetails(parseInt(groupId, 10));
    } catch (error) {
      console.error('Failed to apply changes for group:', groupId, error);
      this.notificationsService.notifyError(
        this.intl.t(SCIM_TRANSLATION_KEYS.ERROR_MESSAGES.GROUP_CHANGES_APPLIED),
      );
    } finally {
      this.isLoading = false;
    }
  }

  @action
  openApplyChangesModal() {
    if (!this.hasUnsavedChanges) {
      return;
    }

    this.includedGroupChanges = Object.keys(this.groupChanges).reduce(
      (acc, groupId) => ({ ...acc, [groupId]: true }),
      {},
    );
    this.includeOrderChanges = !arraysEqual(this.originalGroupOrder, this.currentGroupOrder);
    this.showApplyChangesModal = true;
  }

  @action
  closeApplyChangesModal() {
    this.showApplyChangesModal = false;
    this.includedGroupChanges = {};
    this.includeOrderChanges = true;
  }

  @action
  toggleGroupChange(groupId: string | number) {
    let groupIdStr = groupId.toString();
    this.includedGroupChanges = {
      ...this.includedGroupChanges,
      [groupIdStr]: !this.includedGroupChanges[groupIdStr],
    };
  }

  @action
  toggleOrderChanges() {
    this.includeOrderChanges = !this.includeOrderChanges;
  }

  @action
  async confirmApplyChanges() {
    if (!this.hasIncludedChanges) {
      this.closeApplyChangesModal();
      return;
    }

    let payload: { order?: number[]; mapping?: Record<string, number | null> } = {};

    if (this.includeOrderChanges && !arraysEqual(this.originalGroupOrder, this.currentGroupOrder)) {
      payload.order = this.currentGroupOrder.map((id) => parseInt(id, 10));
    }

    let includedMappingChanges = Object.keys(this.groupChanges).filter(
      (groupIdStr) => this.includedGroupChanges[groupIdStr],
    );

    if (includedMappingChanges.length > 0) {
      payload.mapping = Object.fromEntries(
        includedMappingChanges.map((groupIdStr) => [
          groupIdStr,
          this.groupChanges[groupIdStr].role_id,
        ]),
      );
    }

    this.isLoading = true;
    try {
      await this.bulkUpdateScimGroups(payload);

      if (this.includeOrderChanges) {
        this.originalGroupOrder = [...this.currentGroupOrder];
      }

      includedMappingChanges.forEach((groupIdStr) => {
        let { [groupIdStr]: _removed, ...remaining } = this.groupChanges;
        this.groupChanges = remaining;
      });

      this.closeApplyChangesModal();

      this.notificationsService.notifyConfirmation(
        this.intl.t(SCIM_TRANSLATION_KEYS.SUCCESS_MESSAGES.CHANGES_APPLIED),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'scim_groups_selective',
        section: 'settings',
        place: 'workspace_teammates',
        applied_order_changes: this.includeOrderChanges,
        applied_group_changes_count: includedMappingChanges.length,
      });

      await this.loadScimGroups();
    } catch (error) {
      console.error('Failed to apply selected SCIM group changes:', error);
      this.notificationsService.notifyError(
        this.intl.t(SCIM_TRANSLATION_KEYS.ERROR_MESSAGES.CHANGES_APPLIED),
      );
    } finally {
      this.isLoading = false;
    }
  }

  @action
  applyChanges() {
    this.openApplyChangesModal();
  }

  @action
  cancelChanges() {
    this.groupChanges = {};
    this.currentGroupOrder = [...this.originalGroupOrder];
  }

  private async loadScimGroups() {
    this.isLoading = true;
    try {
      let scimGroups = await this.store.query('scim-group', {
        app_id: this.session.workspace.id,
        sort: 'priority',
      });

      this.scimGroups = scimGroups.slice().sort((a, b) => a.priority - b.priority);
      let sortedOrder = this.scimGroups.map((group) => group.id);
      this.originalGroupOrder = [...sortedOrder];
      this.currentGroupOrder = [...sortedOrder];
      this.groupChanges = {};
    } catch (error) {
      console.error('Failed to load SCIM groups:', String(error));
      this.scimGroups = [];
      this.originalGroupOrder = [];
      this.currentGroupOrder = [];
      this.notificationsService.notifyError(this.intl.t(SCIM_TRANSLATION_KEYS.TAB.LOAD_ERROR));
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCurrentAdminGroups() {
    this.isLoadingUserGroups = true;
    try {
      let currentAdminId = this.appService.app.currentAdmin.id;
      let appId = this.session.workspace.id;

      let result = await taskFor(this.scimGroupService.fetchUserGroups).perform(
        appId,
        currentAdminId,
      );

      if (result && result.groups) {
        this.currentAdminGroups = result.groups;
      } else {
        this.currentAdminGroups = [];
      }
    } catch (error) {
      console.warn('Failed to load current admin groups:', String(error));
      this.currentAdminGroups = [];
    } finally {
      this.isLoadingUserGroups = false;
    }
  }

  get topPriorityGroup() {
    return this.scimGroupService.getTopPriorityGroup(this.currentAdminGroups);
  }

  @action
  isCurrentAdminInGroup(groupId: string): boolean {
    let groupIdInt = parseInt(groupId, 10);
    return this.currentAdminGroups.some((group) => group.id === groupIdInt);
  }

  @action
  isTopPriorityGroupForCurrentAdmin(groupId: string): boolean {
    let groupIdInt = parseInt(groupId, 10);
    let topGroup = this.topPriorityGroup;
    return Boolean(topGroup && topGroup.id === groupIdInt);
  }

  private async loadGroupDetails(groupId: number) {
    let groupDetails = await this.store.findRecord('scim-group', groupId, {
      include: 'members',
      reload: true,
    });

    let groupIndex = this.scimGroups.findIndex((g) => parseInt(g.id, 10) === groupId);
    if (groupIndex !== -1) {
      this.scimGroups[groupIndex] = groupDetails;
      this.scimGroups = [...this.scimGroups];
    }

    if (this.selectedGroupForDetails && parseInt(this.selectedGroupForDetails.id, 10) === groupId) {
      this.selectedGroupForDetails = groupDetails;
    }

    return groupDetails;
  }

  private async bulkUpdateScimGroups(payload: object) {
    await patchRequest(`/ember/scim_group/?app_id=${this.session.workspace.id}`, payload);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Teammates::Scim': typeof Scim;
    'new-settings/workspace/teammates/scim': typeof Scim;
  }
}
